import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaLandingPageTemplateV1Service } from '../service';
import { ListLandingPageTemplatesIO } from '../types';

export const getListLandingPageTemplatesQueryKey = (req: ListLandingPageTemplatesIO['input']) =>
  ['LandingPageTemplateV1', 'ListLandingPageTemplates', req] as const;
type QueryKey = ReturnType<typeof getListLandingPageTemplatesQueryKey>;

/**
 * A hook that returns a query for the `ListLandingPageTemplates` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListLandingPageTemplatesQuery = <E = unknown, D = ListLandingPageTemplatesIO['output']>(
  req: ListLandingPageTemplatesIO['input'],
  options?: LimitedSchemaQueryOptions<ListLandingPageTemplatesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<ListLandingPageTemplatesIO['output'], E, D, QueryKey>({
    queryKey: getListLandingPageTemplatesQueryKey(req),
    queryFn: () => SchemaLandingPageTemplateV1Service.ListLandingPageTemplates(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `ListLandingPageTemplates` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListLandingPageTemplatesQueries = <E = unknown, D = ListLandingPageTemplatesIO['output']>(
  argsArr: {
    req: ListLandingPageTemplatesIO['input'];
    options?: LimitedSchemaQueryOptions<ListLandingPageTemplatesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getListLandingPageTemplatesQueryKey(req),
      queryFn: () => SchemaLandingPageTemplateV1Service.ListLandingPageTemplates(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useListLandingPageTemplatesQuery<E, D>>[];
